<template>
    <div v-if="hasNavigationContext" class="entity-navigation d-flex align-center">
        <!-- Posizione corrente -->
        <span class="navigation-position text-caption grey--text mr-2">
            {{ currentPosition }} di {{ totalItems }}
        </span>

        <!-- Pulsante Previous -->
        <v-tooltip bottom>
            <template v-slot:activator="{ on }">
                <v-btn
                    v-on="on"
                    icon
                    small
                    :disabled="!hasPrevious || loading"
                    :loading="loadingPrev"
                    @click="navigatePrevious"
                >
                    <v-icon>mdi-chevron-left</v-icon>
                </v-btn>
            </template>
            <span>Precedente</span>
        </v-tooltip>

        <!-- Pulsante Next -->
        <v-tooltip bottom>
            <template v-slot:activator="{ on }">
                <v-btn
                    v-on="on"
                    icon
                    small
                    :disabled="!hasNext || loading"
                    :loading="loadingNext"
                    @click="navigateNext"
                >
                    <v-icon>mdi-chevron-right</v-icon>
                </v-btn>
            </template>
            <span>Successivo</span>
        </v-tooltip>

        <!-- Pulsante Torna alla lista -->
        <v-tooltip bottom>
            <template v-slot:activator="{ on }">
                <v-btn
                    v-on="on"
                    icon
                    small
                    @click="backToList"
                >
                    <v-icon>mdi-format-list-bulleted</v-icon>
                </v-btn>
            </template>
            <span>Torna alla lista</span>
        </v-tooltip>
    </div>
</template>

<script>
export default {
    name: 'EntityNavigation',

    props: {
        modelDef: {
            type: String,
            required: true
        },
        currentId: {
            type: Number,
            required: true
        }
    },

    data() {
        return {
            context: null,
            loading: false,
            loadingPrev: false,
            loadingNext: false
        }
    },

    computed: {
        hasNavigationContext() {
            return this.context !== null && this.context.ids && this.context.ids.length > 0
        },

        currentIndex() {
            if (!this.context || !this.context.ids) return -1
            return this.context.ids.indexOf(this.currentId)
        },

        currentPosition() {
            if (!this.context || this.currentIndex === -1) return 0
            // Calcola la posizione globale
            const startPage = this.context.startPage || 1
            const pageOffset = (startPage - 1) * this.context.queryParams.per_page
            return pageOffset + this.currentIndex + 1
        },

        totalItems() {
            return this.context?.totalItems || 0
        },

        hasPrevious() {
            if (!this.context) return false
            // Ha precedente se non e il primo elemento della prima pagina
            return this.currentPosition > 1
        },

        hasNext() {
            if (!this.context) return false
            // Ha successivo se non e l'ultimo elemento
            return this.currentPosition < this.totalItems
        }
    },

    watch: {
        currentId: {
            handler() {
                this.loadContext()
            },
            immediate: true
        },
        modelDef: {
            handler() {
                this.loadContext()
            },
            immediate: true
        }
    },

    methods: {
        loadContext() {
            const stored = sessionStorage.getItem(`navigation_context_${this.modelDef}`)
            if (stored) {
                try {
                    this.context = JSON.parse(stored)
                    // Valida che il context non sia troppo vecchio (30 minuti)
                    if (Date.now() - this.context.timestamp > 30 * 60 * 1000) {
                        this.clearContext()
                    }
                } catch (e) {
                    this.clearContext()
                }
            } else {
                this.context = null
            }
        },

        clearContext() {
            sessionStorage.removeItem(`navigation_context_${this.modelDef}`)
            this.context = null
        },

        async navigatePrevious() {
            if (!this.hasPrevious || this.loading) return

            const localIndex = this.currentIndex

            if (localIndex > 0) {
                // Navigazione locale
                const prevId = this.context.ids[localIndex - 1]
                this.navigateToEntity(prevId)
            } else {
                // Serve caricare la pagina precedente
                await this.loadPreviousPage()
            }
        },

        async navigateNext() {
            if (!this.hasNext || this.loading) return

            const localIndex = this.currentIndex

            if (localIndex < this.context.ids.length - 1) {
                // Navigazione locale
                const nextId = this.context.ids[localIndex + 1]
                this.navigateToEntity(nextId)
            } else {
                // Serve caricare la pagina successiva
                await this.loadNextPage()
            }
        },

        async loadPreviousPage() {
            const startPage = this.context.startPage || 1
            if (startPage <= 1) return

            this.loadingPrev = true
            this.loading = true

            try {
                const newPage = startPage - 1
                const response = await this.fetchPage(newPage)

                // Prepend i nuovi ID
                const newIds = response.data.data.data.map(i => i.id)
                this.context.ids = [...newIds, ...this.context.ids]
                this.context.startPage = newPage

                // Naviga all'ultimo elemento della nuova pagina
                const lastNewId = newIds[newIds.length - 1]
                this.saveContext()
                this.navigateToEntity(lastNewId)
            } catch (error) {
                console.error('Errore caricamento pagina precedente:', error)
            } finally {
                this.loadingPrev = false
                this.loading = false
            }
        },

        async loadNextPage() {
            const perPage = this.context.queryParams.per_page
            const maxPage = Math.ceil(this.context.totalItems / perPage)
            const currentMaxPage = this.context.currentMaxPage || this.context.startPage || 1

            if (currentMaxPage >= maxPage) return

            this.loadingNext = true
            this.loading = true

            try {
                const newPage = currentMaxPage + 1
                const response = await this.fetchPage(newPage)

                // Append i nuovi ID
                const newIds = response.data.data.data.map(i => i.id)
                this.context.ids = [...this.context.ids, ...newIds]
                this.context.currentMaxPage = newPage

                // Naviga al primo elemento della nuova pagina
                const firstNewId = newIds[0]
                this.saveContext()
                this.navigateToEntity(firstNewId)
            } catch (error) {
                console.error('Errore caricamento pagina successiva:', error)
            } finally {
                this.loadingNext = false
                this.loading = false
            }
        },

        async fetchPage(page) {
            const params = {
                ...this.context.queryParams,
                page: page
            }
            // Usa filterEndpoint salvato nel context, altrimenti fallback a modelDef
            const endpoint = this.context.filterEndpoint || this.modelDef
            return this.axios.post(`${endpoint}-filters`, params)
        },

        navigateToEntity(id) {
            // Aggiorna il context
            this.context.currentId = id
            this.saveContext()

            // Naviga alla nuova entita usando il route salvato nel context
            const routeName = this.context.showRoute || `${this.modelDef}-show`
            const routeParams = this.context.routeParams || {}
            this.$router.push({ name: routeName, params: { id: id, ...routeParams } })
        },

        saveContext() {
            this.context.timestamp = Date.now()
            sessionStorage.setItem(
                `navigation_context_${this.modelDef}`,
                JSON.stringify(this.context)
            )
        },

        backToList() {
            if (this.context?.listRoute) {
                this.$router.push(this.context.listRoute)
            } else {
                // Fallback: vai all'index del modelDef
                this.$router.push({ name: `${this.modelDef}-index` })
            }
        }
    }
}
</script>

<style scoped>
.entity-navigation {
    margin-left: 8px
}
.navigation-position {
    min-width: 60px;
    text-align: center
}
</style>
